import { checkInModel, FIRST_CLASS_LABEL, settledCheckInTaps, withCheckInTaps, type AttendanceMark, type CheckInState, type CheckInTap } from "@teamna/shared";
import { useEffect, useState } from "react";
import { Avatar } from "./Avatar.tsx";
import { Badge, type BadgeTone } from "./Badge.tsx";
import { CheckIcon } from "./Icons.tsx";
import { InlineError } from "./Notice.tsx";
import { Notice } from "./Section.tsx";

/**
 * Check-in by name (I1): the class list of a class where clients pick each class. The instructor
 * taps each client who comes. A tap saves at once (`onTap`), so there is no Save button. A second
 * tap takes the check-in back. A tap on an absent client (also one the job marked) checks them in,
 * late. The words come from `checkInModel` in `@teamna/shared`, so the phone says the same.
 *
 * The rows are the booked people only. The cards and the list stay one tap away at the top of the
 * screen (`TakeAttendance`), for a register of the whole class.
 */

export type CheckInPerson = {
  bookingId: string;
  name: string;
  note: string | null;
  photoUrl?: string | null;
  attendance?: AttendanceMark | null;
  checkedInAt?: string | null;
  autoAbsent?: boolean;
  firstClass?: boolean;
};

export type CheckInListProps = {
  people: CheckInPerson[];
  startsAt: Date;
  /** When the job marks the rest absent. Null: the business turned it off. */
  autoAbsentAt: Date | null;
  timeZone?: string;
  participantLabel: { one: string; many: string };
  /** Save one tap: present checks in, null takes the check-in back. Resolves when it is stored. */
  onTap: (person: CheckInPerson, next: "present" | null) => Promise<void>;
  /** The clock. Stories and tests fix it; the screen reads it again every 15 seconds. */
  now?: () => Date;
};

const STATE_TONE: Record<CheckInState, BadgeTone> = { here: "confirmed", late: "late", absent: "cancelled", waiting: "quiet" };

function useClock(now: () => Date): Date {
  const [time, setTime] = useState(now);
  useEffect(() => {
    const id = window.setInterval(() => setTime(now()), 15_000);
    return () => window.clearInterval(id);
  }, [now]);
  return time;
}

const realNow = () => new Date();

export function CheckInList({ people, startsAt, autoAbsentAt, timeZone, participantLabel, onTap, now = realNow }: CheckInListProps) {
  const clock = useClock(now);
  // A tap shows at once (`overlay`); the stored mark takes over when the session is read again.
  const [overlay, setOverlay] = useState<Record<string, CheckInTap>>({});
  const [saving, setSaving] = useState<Record<string, true>>({});
  const [error, setError] = useState<unknown>(null);

  // The stored mark caught up with a tap: the tap's own picture goes. React's "adjust state while
  // rendering" pattern, so a later change from another device is never hidden by an old tap.
  const caughtUp = settledCheckInTaps(people, overlay, saving);
  if (caughtUp.length > 0) {
    const rest = { ...overlay };
    for (const id of caughtUp) delete rest[id];
    setOverlay(rest);
  }

  const shown = withCheckInTaps(people, overlay);
  const model = checkInModel({
    startsAt,
    now: clock,
    autoAbsent: autoAbsentAt,
    roster: shown,
    participantLabel,
    ...(timeZone ? { timeZone } : {}),
  });

  async function tap(person: CheckInPerson, next: "present" | null) {
    if (!model.canTap || saving[person.bookingId]) return;
    const id = person.bookingId;
    setError(null);
    setSaving((current) => ({ ...current, [id]: true }));
    setOverlay((current) => ({ ...current, [id]: { attendance: next, at: now().toISOString() } }));
    try {
      await onTap(person, next);
    } catch (caught) {
      setError(caught);
      setOverlay((current) => {
        const rest = { ...current };
        delete rest[id];
        return rest;
      });
    } finally {
      setSaving((current) => {
        const rest = { ...current };
        delete rest[id];
        return rest;
      });
    }
  }

  const tone = model.phase === "started" ? "warning" : model.phase === "closed" && model.counts.waiting === 0 ? "success" : "info";
  return (
    <div className="flex flex-col gap-2 pb-4">
      <Notice tone={tone} title={model.title}>
        {model.text}
      </Notice>
      <p className="px-1 pt-1 text-[13px] font-semibold text-ink-2" aria-live="polite">
        {model.summary}
      </p>
      {people.length === 0 ? (
        <p className="rounded-card border border-line bg-paper p-5 text-[15px] text-ink-2">Nobody is booked in this session.</p>
      ) : null}
      {shown.map((person, index) => {
        const row = model.rows[index]!;
        const busy = Boolean(saving[person.bookingId]);
        const action = row.tap === "check_in" ? `Check in ${person.name}` : `Undo the check-in of ${person.name}`;
        return (
          <button
            key={person.bookingId}
            type="button"
            disabled={!model.canTap}
            aria-busy={busy || undefined}
            aria-label={`${action}. ${row.label}`}
            onClick={() => void tap(person, row.tap === "check_in" ? "present" : null)}
            data-state={row.state}
            className="flex min-h-15 w-full items-center gap-2.5 rounded-xl border border-line bg-paper px-2.5 py-2 text-left transition-colors enabled:active:bg-sunken disabled:opacity-60 data-[state=here]:border-confirmed/40 data-[state=late]:border-late/40"
          >
            <Avatar name={person.name} photoUrl={person.photoUrl ?? null} size={38} />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span dir="auto" className="truncate text-[15px] font-semibold text-ink">
                {person.name}
              </span>
              {person.firstClass || person.note ? (
                <span className="flex flex-wrap gap-1">
                  {person.firstClass ? <Badge tone="info">{FIRST_CLASS_LABEL}</Badge> : null}
                  {person.note ? (
                    <span dir="auto" className="rounded-full bg-late-surface px-2 py-px text-[11.5px] font-semibold text-late-ink">
                      {person.note}
                    </span>
                  ) : null}
                </span>
              ) : null}
              {row.detail ? <span className="text-[12px] text-ink-2">{row.detail}</span> : null}
            </span>
            <Badge tone={STATE_TONE[row.state]}>{row.label}</Badge>
            <span
              aria-hidden="true"
              className={`grid size-9 shrink-0 place-items-center rounded-full border ${row.tap === "undo" ? "border-confirmed bg-confirmed text-inverse" : "border-line-strong bg-paper text-ink-3"}`}
            >
              <CheckIcon size={18} />
            </span>
          </button>
        );
      })}
      {error ? <InlineError error={error} /> : null}
    </div>
  );
}
